import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, Gift, Mail } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const dismissed = useRef(false);
  const triggered = useRef(false);
  const pageLoadTime = useRef(Date.now());

  useEffect(() => {
    // Check if already dismissed this session or coming from purchase
    if (sessionStorage.getItem('exit_popup_dismissed')) {
      dismissed.current = true;
      return;
    }
    // Suppress after purchase success
    if (window.location.search.includes('session_id') || window.location.pathname.includes('success')) {
      dismissed.current = true;
      sessionStorage.setItem('exit_popup_dismissed', 'true');
      return;
    }

    const MIN_TIME_ON_PAGE = 30000; // 30 seconds minimum before showing

    const canShow = () => {
      return !dismissed.current && !triggered.current && (Date.now() - pageLoadTime.current >= MIN_TIME_ON_PAGE);
    };

    // Desktop: true exit intent — mouse leaves viewport toward top
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && canShow()) {
        triggered.current = true;
        setShow(true);
      }
    };

    // Delay adding desktop listener (20s after page load)
    const desktopTimer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 20000);

    // Mobile: trigger on visibility change (user switching away from tab/app)
    // Only after minimum time on page
    const handleVisibilityChange = () => {
      if (document.hidden && canShow()) {
        // Show when they come back
        const handleReturn = () => {
          if (!document.hidden && canShow()) {
            triggered.current = true;
            setShow(true);
          }
          document.removeEventListener('visibilitychange', handleReturn);
        };
        document.addEventListener('visibilitychange', handleReturn);
      }
    };

    const isMobile = window.innerWidth < 768;
    let mobileVisTimer;
    if (isMobile) {
      mobileVisTimer = setTimeout(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }, MIN_TIME_ON_PAGE);
    }

    return () => {
      clearTimeout(desktopTimer);
      clearTimeout(mobileVisTimer);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleDismiss = () => {
    setShow(false);
    dismissed.current = true;
    sessionStorage.setItem('exit_popup_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/lead-capture`, { email });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="exit-intent-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="exit-popup-close"
        >
          <X size={20} />
        </button>

        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500" />

        <div className="p-6 md:p-8">
          {status !== 'success' ? (
            <>
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                  <Gift className="w-7 h-7 text-orange-600" />
                </div>
              </div>

              {/* Headline */}
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-2" data-testid="exit-popup-title">
                Wait — grab your free guide
              </h2>
              <p className="text-gray-600 text-sm text-center mb-5 leading-relaxed">
                Before you go, download our <span className="font-semibold text-gray-800">How, When & Why to File a Lawsuit Guide</span> — completely free. No credit card needed.
              </p>

              {/* What's inside */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5 space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Inside this guide:</p>
                {[
                  'When to file a lawsuit vs. when to settle',
                  'Step-by-step instructions for small claims court',
                  'Real legal templates you can use immediately',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    </div>
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              {/* Email form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  data-testid="exit-popup-email"
                  required
                />
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-5 text-base font-semibold hover:from-orange-600 hover:to-red-700 shadow-lg"
                  disabled={status === 'loading'}
                  data-testid="exit-popup-submit"
                >
                  {status === 'loading' ? 'Sending...' : 'Send Me the Free Guide'}
                </Button>
              </form>

              {status === 'error' && (
                <p className="text-red-500 text-xs text-center mt-2">Something went wrong. Please try again.</p>
              )}

              <p className="text-[10px] text-gray-400 text-center mt-3">
                We respect your privacy. Unsubscribe anytime.
              </p>

              {/* Skip link */}
              <button
                onClick={handleDismiss}
                className="block w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-3 transition-colors"
                data-testid="exit-popup-skip"
              >
                No thanks, I'll pass
              </button>
            </>
          ) : (
            /* Success state */
            <div className="text-center py-4">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="exit-popup-success-title">
                Check Your Email!
              </h2>
              <p className="text-gray-600 text-sm mb-5">
                We've sent your free guide to <strong>{email}</strong>. Check your inbox for the download link.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-green-800 font-medium">Your download link has been emailed to you.</p>
              </div>
              <button
                onClick={handleDismiss}
                className="block w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-4 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
